import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { priceTintLine, VLT_OPTIONS, SIZE_CLASS_TO_SIZE_BUCKET } from '@wrapforce/shared';
import type { InventoryItem, SizeClass, TintPricingInput } from '@wrapforce/shared';
import type { TintPriceMatrixCell } from '@wrapforce/shared/schemas/pricing';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import type { DraftLine } from '../NewQuotePage';
import {
  useTintPricingProfile, useVehicleCatalogTrimDetail, useWorkspaceVehicleById,
  type VehiclePricingSource,
} from './pricingApi';
import { useTintZoneDefs, useVehicleServiceQuantities, usePutVehicleServiceQuantity } from '@/features/settings/pricing-v2/api';
import { useBrands, useInventory } from '@/features/inventory/api';
import { useLocaleFormatters } from '@/lib/format';
import {
  VehiclePicker, vehicleSelectionFromTrimDetail, vehicleSelectionFromWorkspaceVehicleItem,
  type CustomerVehicleHint, type VehicleSelection,
} from './VehiclePicker';
import { PricingBreakdownSidebar } from './PricingBreakdownSidebar';
import { AddInventoryItemDialog } from './AddInventoryItemDialog';

type TintZoneKey = TintPriceMatrixCell['zoneKey'];
type TintFilmGrade = TintPriceMatrixCell['filmGrade'];

const TINT_ZONE_KEYS: TintZoneKey[] = ['front_two', 'all_around', 'full_incl_windshield', 'windshield_strip', 'sunroof'];

// Translation keys (see PPF_ZONE_LABEL_KEYS in PpfLineBuilder.tsx for the
// same rationale) - consumed by the <option> labels below and by
// NewQuotePage's line summary.
export const TINT_ZONE_LABEL_KEYS: Record<TintZoneKey, string> = {
  front_two: 'pricing.tint.zone.front_two',
  all_around: 'pricing.tint.zone.all_around',
  full_incl_windshield: 'pricing.tint.zone.full_incl_windshield',
  windshield_strip: 'pricing.tint.zone.windshield_strip',
  sunroof: 'pricing.tint.zone.sunroof',
};

export const TINT_FILM_GRADE_LABEL_KEYS: Record<TintFilmGrade, string> = {
  dyed: 'pricing.tint.filmGrade.dyed',
  carbon: 'pricing.tint.filmGrade.carbon',
  ceramic: 'pricing.tint.filmGrade.ceramic',
};

export interface TintLineOutput {
  serviceKind: 'tint';
  zoneKey: TintZoneKey;
  sizeClass: SizeClass;
  filmGrade?: TintFilmGrade;
  tintProductId?: string;
  vlt: number;
  estimatedMinutes: number;
  lineTotalCents: number;
  breakdown: Record<string, unknown>;
  profileVersionId: string;
  vehicleLabel: string;
  vehicleCatalogTrimId: string | null;
  workspaceVehicleId: string | null;
  materialInventoryItemId?: string | null;
  materialQty?: number | null;
  materialUnit?: 'sqft' | null;
  reservedMaterialCostCents?: number | null;
}

export interface TintDraftState {
  vehicle: VehicleSelection | null;
  zoneKey: TintZoneKey;
  vlt: number;
  materialItemId: string;
  manualTintQty: string;
}

export function TintLineBuilder({
  workspaceId, onAddLine, initialState, onStateChange, customerVehicleHint = null, customerId = null, editingLine = null, onCancelEdit,
}: {
  workspaceId: string | null;
  onAddLine: (line: TintLineOutput) => void;
  // See VehicleAwareWrapLineBuilder: restore-on-mount + serialized-state emit
  // for the in-progress builder so leaving/reopening restores it open.
  initialState?: Record<string, unknown>;
  onStateChange?: (state: Record<string, unknown>) => void;
  customerVehicleHint?: CustomerVehicleHint | null;
  customerId?: string | null;
  editingLine?: DraftLine | null;
  onCancelEdit?: () => void;
}) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  const { data: profile } = useTintPricingProfile(workspaceId);
  const { data: zoneDefs } = useTintZoneDefs(workspaceId);

  // Tint film now comes from Inventory (category = 'tint'), same as ceramic's
  // MaterialPicker-style brand/item source - not a separate tint-product catalog.
  const { data: inventoryData } = useInventory(workspaceId, { category: 'tint' });
  const tintItems = inventoryData?.items ?? [];
  const { data: brandsData } = useBrands(workspaceId);
  const brandNameById = new Map((brandsData?.items ?? []).map((b): [string, string] => [b.id, b.name]));
  const itemBrandName = (item: InventoryItem) => (item.brandId && brandNameById.get(item.brandId)) || t('pricing.noBrand');

  const editingCoverage = editingLine?.coverage?.serviceKind === 'tint' ? editingLine.coverage : null;
  const restore = (editingLine ? undefined : initialState) as Partial<TintDraftState> | undefined;

  const [vehicle, setVehicle] = useState<VehicleSelection | null>(restore?.vehicle ?? null);
  const [zoneKey, setZoneKey] = useState<TintZoneKey>(editingCoverage?.zoneKey ?? restore?.zoneKey ?? 'front_two');
  const [vlt, setVlt] = useState<number>(editingCoverage?.vlt ?? restore?.vlt ?? VLT_OPTIONS[0]);
  const [materialItemId, setMaterialItemId] = useState<string>(editingLine?.materialInventoryItemId ?? restore?.materialItemId ?? '');
  const [addInventoryOpen, setAddInventoryOpen] = useState(false);
  // Tracks an item created via the inline dialog so the "default to first
  // item" effect below doesn't clobber the selection once useInventory
  // refetches and the new item appears in tintItems.
  const [pendingSelectItemId, setPendingSelectItemId] = useState<string | null>(null);

  // See VehicleAwareWrapLineBuilder.tsx's identical effect for why: an edited
  // line only carries the vehicle's ids, not its full VehicleSelection.
  const { data: trimDetail } = useVehicleCatalogTrimDetail(workspaceId, editingLine?.vehicleCatalogTrimId ?? undefined);
  const { data: workspaceVehicleDetail } = useWorkspaceVehicleById(workspaceId, editingLine?.workspaceVehicleId ?? undefined);
  const resolvingEditVehicle = !!(editingLine?.vehicleCatalogTrimId || editingLine?.workspaceVehicleId) && !vehicle;

  useEffect(() => {
    if (vehicle) return;
    const resolved = trimDetail
      ? vehicleSelectionFromTrimDetail(trimDetail)
      : workspaceVehicleDetail
        ? vehicleSelectionFromWorkspaceVehicleItem(workspaceVehicleDetail)
        : null;
    if (resolved) setVehicle(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimDetail, workspaceVehicleDetail]);

  const sizeBucket = vehicle ? SIZE_CLASS_TO_SIZE_BUCKET[vehicle.sizeClass] : null;
  const zoneDef = zoneDefs?.find((zd) => zd.zoneKey === zoneKey && zd.sizeClass === vehicle?.sizeClass);

  // Default to the first stocked brand/item once inventory loads, correcting
  // away from a selection that no longer exists (mirrors CeramicLineBuilder's
  // identical "keep the item selection valid" effect).
  useEffect(() => {
    if (!inventoryData) return;
    if (pendingSelectItemId && tintItems.some((i) => i.id === pendingSelectItemId)) {
      setMaterialItemId(pendingSelectItemId);
      setPendingSelectItemId(null);
      return;
    }
    if (materialItemId && tintItems.some((i) => i.id === materialItemId)) return;
    setMaterialItemId(tintItems[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData]);

  const selectedItem = tintItems.find((i) => i.id === materialItemId);
  const selectedBrand = selectedItem ? itemBrandName(selectedItem) : '';
  const brandOptions = Array.from(new Set(tintItems.map(itemBrandName))).sort();
  const itemOptionsForBrand = tintItems.filter((i) => itemBrandName(i) === selectedBrand);

  function handleBrandChange(brand: string) {
    const firstMatch = tintItems.find((i) => itemBrandName(i) === brand);
    setMaterialItemId(firstMatch?.id ?? '');
  }

  // Material consumption (§material-consumption): see PpfLineBuilder.tsx's
  // identical block for the full rationale.
  const vehiclePricingSource: VehiclePricingSource | null = vehicle?.catalogVehicleId ? 'catalog' : vehicle?.workspaceVehicleId ? 'workspace' : null;
  const vehiclePricingId = vehiclePricingSource === 'catalog' ? vehicle?.catalogVehicleId : vehicle?.workspaceVehicleId;
  const { data: serviceQuantities } = useVehicleServiceQuantities(
    workspaceId,
    vehiclePricingSource,
    vehiclePricingId ?? null,
  );
  const putServiceQuantity = usePutVehicleServiceQuantity(workspaceId, vehiclePricingSource, vehiclePricingId ?? null);
  const storedTintQty = serviceQuantities?.tint ?? null;
  const [manualTintQty, setManualTintQty] = useState<string>(restore?.manualTintQty ?? '');
  const materialQty = storedTintQty ?? (manualTintQty ? Number(manualTintQty) : null);
  const materialQtyValid = materialQty !== null && materialQty > 0;

  const materialCostCents = selectedItem && materialQtyValid
    ? Math.round(materialQty! * selectedItem.priceCentsPerUnit)
    : 0;

  // Tint's engine is a flat passthrough (lineSubtotalCents === priceCents +
  // designCostCents + fixedAddOnCents), so the material cost from Inventory
  // becomes priceCents directly - there is no separate flat matrix/product
  // price added on top (unlike ceramic, where labor and material are summed).
  let v3Input: TintPricingInput | null = null;
  if (vehicle && sizeBucket && zoneDef && selectedItem && materialQtyValid) {
    v3Input = {
      zoneMinutes: zoneDef.minutes,
      priceCents: materialCostCents,
      vlt,
      designMinutes: 0,
      fixedAddOnCents: 0,
    };
  }

  let result: ReturnType<typeof priceTintLine> | null = null;
  if (v3Input && profile) result = priceTintLine(v3Input, profile.resolved);

  const canAdd = !!vehicle && !!v3Input && !!result && !!profile && !!selectedItem && materialQtyValid;

  // Single source of truth for this line's output - see VehicleAwareWrapLineBuilder.
  const liveOutput: TintLineOutput | null = canAdd && vehicle && result && profile && selectedItem
    ? {
        serviceKind: 'tint',
        zoneKey,
        sizeClass: vehicle.sizeClass,
        vlt,
        estimatedMinutes: result.estimatedMinutes,
        lineTotalCents: result.lineSubtotalCents,
        breakdown: result.breakdown as unknown as Record<string, unknown>,
        profileVersionId: profile.versionId,
        vehicleLabel: vehicle.label,
        vehicleCatalogTrimId: vehicle.catalogTrimId,
        workspaceVehicleId: vehicle.workspaceVehicleId,
        materialInventoryItemId: selectedItem.id,
        materialQty,
        materialUnit: 'sqft',
        reservedMaterialCostCents: materialCostCents > 0 ? materialCostCents : null,
      }
    : null;

  // Persist raw builder state (add mode, vehicle picked) - see VehicleAwareWrapLineBuilder.
  const draftState: TintDraftState = { vehicle, zoneKey, vlt, materialItemId, manualTintQty };
  const draftStateKey = JSON.stringify(draftState);
  useEffect(() => {
    if (editingLine || !vehicle) return;
    onStateChange?.(draftState as unknown as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStateKey, editingLine]);

  function handleAddLine() {
    if (!liveOutput) return;
    // Persist the entered sqft back as this vehicle's default tint quantity, but
    // only on the explicit Add (not on every keystroke).
    if (storedTintQty === null && vehiclePricingSource && vehiclePricingId) {
      putServiceQuantity.mutate({ kind: 'tint', input: { quantity: materialQty! } });
    }
    onAddLine(liveOutput);
    toast.success(editingLine ? t('pricing.lineUpdatedToast') : t('pricing.tint.toastAdded'));

    if (!editingLine) {
      setVehicle(null);
      setZoneKey('front_two');
      setVlt(VLT_OPTIONS[0]);
      setManualTintQty('');
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.vehicle')}</h3>
        {resolvingEditVehicle ? (
          <p className="text-sm text-muted">{t('pricing.loadingVehicle')}</p>
        ) : (
          <VehiclePicker workspaceId={workspaceId} selection={vehicle} onSelect={setVehicle} customerVehicleHint={customerVehicleHint} customerId={customerId} />
        )}
      </section>

      {vehicle ? (
        <>
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.tintZoneGradeVlt')}</h3>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="tint-zone">{t('pricing.tint.zoneLabel')}</Label>
                <select
                  id="tint-zone"
                  value={zoneKey}
                  onChange={(e) => setZoneKey(e.target.value as TintZoneKey)}
                  className="h-9 border border-border bg-surface px-2 text-sm"
                >
                  {TINT_ZONE_KEYS.map((zk) => (
                    <option key={zk} value={zk}>{t(TINT_ZONE_LABEL_KEYS[zk])}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="tint-vlt">{t('pricing.tint.vltLabel')}</Label>
                <select
                  id="tint-vlt"
                  value={vlt}
                  onChange={(e) => setVlt(Number(e.target.value))}
                  className="h-9 border border-border bg-surface px-2 text-sm"
                >
                  {VLT_OPTIONS.map((v) => (
                    <option key={v} value={v}>{t('pricing.tint.vltOption', { vlt: v })}</option>
                  ))}
                </select>
              </div>
            </div>
            {!zoneDef ? (
              <p className="text-sm text-muted">{t('pricing.tint.noPricingData')}</p>
            ) : null}
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.materialCoverage')}</h3>
              <button type="button" onClick={() => setAddInventoryOpen(true)} className="text-[11px] font-semibold text-accent-ink hover:underline">
                {t('pricing.material.addToInventory')}
              </button>
            </div>
            {!inventoryData ? (
              <p className="text-xs text-muted">{t('pricing.material.loadingStocked')}</p>
            ) : tintItems.length === 0 ? (
              <div className="flex flex-col gap-1.5 border border-border p-3">
                <p className="text-sm text-muted">{t('pricing.tint.noInventoryItems')}</p>
                <Link to="/inventory" className="w-fit text-xs font-semibold text-accent-ink underline">
                  {t('pricing.tint.addToInventoryLink')}
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="tint-brand">{t('pricing.tint.brandLabel')}</Label>
                    <select
                      id="tint-brand"
                      value={selectedBrand}
                      onChange={(e) => handleBrandChange(e.target.value)}
                      className="h-9 border border-border bg-surface px-2 text-sm"
                    >
                      {brandOptions.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="tint-item">{t('pricing.tint.itemLabel')}</Label>
                    <select
                      id="tint-item"
                      value={materialItemId}
                      onChange={(e) => setMaterialItemId(e.target.value)}
                      className="h-9 border border-border bg-surface px-2 text-sm"
                    >
                      {itemOptionsForBrand.map((i) => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {storedTintQty !== null ? (
                  <p className="text-sm text-ink">{t('pricing.materialConsumption.tintNeededLabel')}: {storedTintQty}</p>
                ) : (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="tint-material-qty">{t('pricing.materialConsumption.tintNeededLabel')}</Label>
                    <Input
                      id="tint-material-qty"
                      type="number"
                      min="0"
                      step="any"
                      value={manualTintQty}
                      onChange={(e) => setManualTintQty(e.target.value)}
                      className="h-9 max-w-[10rem]"
                    />
                  </div>
                )}
                {selectedItem && materialQtyValid ? (
                  <p className="text-xs text-muted">
                    {t('pricing.materialConsumption.breakdownRow', {
                      qty: materialQty,
                      unit: selectedItem.unit,
                      price: formatMoney(selectedItem.priceCentsPerUnit),
                      cost: formatMoney(materialCostCents),
                    })}
                  </p>
                ) : null}
              </>
            )}
          </section>

          {result ? <PricingBreakdownSidebar result={result} /> : null}

          <div className="flex items-center gap-2">
            <Button type="button" disabled={!canAdd} onClick={handleAddLine}>
              {editingLine ? t('pricing.saveChanges') : t('pricing.addService')}
            </Button>
            {editingLine ? (
              <Button type="button" variant="secondary" onClick={onCancelEdit}>
                {t('pricing.cancelEdit')}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted">{t('pricing.pickVehicle')}</p>
      )}

      <AddInventoryItemDialog
        open={addInventoryOpen}
        onOpenChange={setAddInventoryOpen}
        workspaceId={workspaceId}
        category="tint"
        onCreated={(itemId) => {
          setPendingSelectItemId(itemId);
          setMaterialItemId(itemId);
        }}
      />
    </div>
  );
}
